import type { AssetManifest, FrameAnimation } from "./manifest.ts";

/**
 * Frame animation timing (ADR-041): pure functions over a manifest animation and a clock, so every cell
 * and prop sharing an animation stays in lockstep, and the strip a frame belongs to.
 */

/**
 * The strip a frame belongs to, whether it starts it or sits inside it, looping or played once (ADR-058):
 * the palette and a placed prop only ever show a strip's first frame, so a panel that edits a frame's own
 * data needs this to reach the others. Scans every animation; for panels, never per frame.
 *
 * @param manifest The asset manifest.
 * @param frame The frame id.
 * @returns The strip's frames in order and the frame's index among them, or null when no strip holds it.
 */
export function stripAround(manifest: AssetManifest, frame: string): { readonly frames: readonly string[]; readonly index: number } | null {
  const starting = manifest.animations[frame];
  if (starting !== undefined) {
    return { frames: starting.frames, index: 0 };
  }
  for (const animation of Object.values(manifest.animations)) {
    const index = animation.frames.indexOf(frame);
    if (index >= 0) {
      return { frames: animation.frames, index };
    }
  }
  return null;
}

/**
 * A strip played backwards (ADR-058's amendment of 2026-10-03): its frames last to first, each keeping its own
 * duration, and everything else it carries as it was. A transition that names a strip `reverse` is laid out
 * this way once, by the compile and by the editor's preview, so nothing that plays it knows a direction.
 *
 * @param strip The strip's frames and the milliseconds each shows, one per frame.
 * @returns A new strip in reverse order; the one given is not changed.
 */
export function reversedStrip<F, T extends { readonly frames: readonly F[]; readonly durations: readonly number[] }>(strip: T): T {
  return { ...strip, frames: [...strip.frames].reverse(), durations: [...strip.durations].reverse() };
}

/**
 * One loop of an animation in milliseconds.
 *
 * @param animation The animation.
 * @returns The sum of its durations.
 */
export function animationPeriod(animation: FrameAnimation): number {
  let total = 0;
  for (const duration of animation.durations) {
    total += duration;
  }
  return total;
}

/**
 * The frame index an animation shows at a time.
 *
 * @param animation The animation.
 * @param elapsedMs Milliseconds on the shared clock; any value, negative included.
 * @returns Index into `animation.frames`.
 */
export function animationFrameAt(animation: FrameAnimation, elapsedMs: number): number {
  const period = animationPeriod(animation);
  let remaining = ((elapsedMs % period) + period) % period;
  for (let i = 0; i < animation.durations.length; i += 1) {
    remaining -= animation.durations[i] as number;
    if (remaining < 0) {
      return i;
    }
  }
  return animation.durations.length - 1;
}
